/**
 * ButtonUI renders a `button` layout element inside a form body.
 *
 * It delegates to the form's Workflow control (`FormWorkflowSelect`), configured by
 * the element:
 * - neither `menuSection` nor `menuItemKey` → the full Workflow dropdown.
 * - `menuSection` only → a dropdown filtered to that section (Share, Workflows, Set
 *   form to, Create new form, Version).
 * - `menuItemKey` → a single direct-action button for that item (e.g. create a
 *   Quote), optionally gated behind a Popconfirm.
 *
 * Workflow-menu actions are form-scoped; the button shows disabled when it has no
 * form context (e.g. rendered inside an entity).
 *
 * Subscribes to one leaf, `form.data.docId` — the id `mergeDocumentIntoProxy`
 * injects, so it is present exactly while the form document is loaded.
 * `useFormStore()` is banned here: this button renders inside the form body, and
 * that hook's spread of `formVersion.data` re-rendered it on every keystroke in
 * the form (SYD-2252).
 */
import { FC, useContext } from 'react';
import { Button } from 'antd';
import { useSnapshot } from 'valtio';
import Icon from '../atoms/Icon';
import type { UIButton } from '#types/System.types';
import { PageScopeContext, formStore } from '../../store';
import FormWorkflowSelect from '../artifacts/form/FormWorkflowSelect';

interface ButtonUIProps {
  /** The button layout element to render. */
  uiElement: UIButton;
  /** Whether the form is read-only/locked. */
  disabled?: boolean;
}

const ButtonUI: FC<ButtonUIProps> = ({ uiElement, disabled = false }) => {
  const pageScope = useSnapshot(useContext(PageScopeContext));
  const insideEntity = pageScope.source === 'entity' && pageScope.entityId !== '';
  const formId = useSnapshot(formStore.state).form.data?.docId;

  const control =
    insideEntity || !formId ? (
      <Button disabled icon={<Icon.ChevronsDown />} aria-label="Workflow">
        {uiElement.label || 'Workflow'}
      </Button>
    ) : (
      <FormWorkflowSelect
        formId={formId}
        section={uiElement.menuSection}
        itemKey={uiElement.menuItemKey}
        label={uiElement.label}
        requiresConfirmation={uiElement.requiresConfirmation}
        disabled={disabled}
      />
    );

  return <div style={{ alignSelf: 'flex-end' }}>{control}</div>;
};

export default ButtonUI;
